import { CURRENCY, LOCALE } from '@/domains/core/settings/constants';
import {
  getPlatform,
  isB2B,
  isUkPlatform,
} from '@/domains/core/settings/utils';
import type { Detail } from '@/domains/productDiscovery/ProductPage/b2c/interfaces/Detail.model';
import type {
  Price,
  ProductPrice,
  Seller,
} from '@/domains/productDiscovery/ProductPage/interfaces/Detail.common.model';
import type { Product } from '@/domains/productDiscovery/Recommendation/interfaces/product';

import type { IProduct } from '../context/ProductBundle/productBundle.interfaces';
import { ECardCurrency } from '../ProductBundle.interfaces';

/**
 * Returns the total price of the products that we have added to the Bundle
 * @param productList Array of products
 * @returns Total price of the products added to the bundle
 */
export const getPrice = (productList: Array<IProduct>): string =>
  productList
    .filter((product) => product.isInBundle)
    .reduce((acc, acum) => acc + acum.bundlePrices.main, 0)
    .toFixed(2);

/**
 * Returns the total price of the products in the Bundle regardless of them being added to the cart or not
 * @param productList Array of products
 * @returns Total price of the products in the bundle
 */
export const getFullBundlePrice = (productList: Array<IProduct>): string =>
  productList
    .reduce((acc, acum) => acc + (acum.bundlePrices?.main || 0), 0)
    .toFixed(2);

/**
 * Returns the currency symbol according to our locale + currency
 * @returns Actual currency symbol
 * @returnExample "€" | "£"
 */
const getCurrency = () =>
  (0)
    .toLocaleString(LOCALE, {
      style: 'currency',
      currency: CURRENCY,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })
    .replace('0', '')
    .trim();

/**
 * Returns the formatted provided amount
 * @param totalPrice Price amount
 * @returns Formatted price
 * @returnExample "120€22" | "£126.94"
 */
export const formatPrice = (totalPrice: string) =>
  isUkPlatform()
    ? `${getCurrency()}${totalPrice.toString()}`
    : `${totalPrice.toString().split('.')[0]}${getCurrency()}${
        totalPrice.toString().split('.')[1]
      }`;
/**
 * Returns the formatted price of all products in the Bundle provideds in productList
 * @param productList Array of products
 * @returns Formatted price
 */
export const getTotalPrice = (productList: Array<IProduct>) => {
  const totalPrice = getPrice(productList);
  const formattedTotalPrice = formatPrice(totalPrice);

  return formattedTotalPrice;
};

/**
 * Returns the total price of the B2B products that we have added to the Bundle
 * @param productList Array of products
 * @returns Total price of the products added to the bundle
 */
export const getTotalB2BPrice = (
  productList: Array<IProduct>,
): { main: number; secondary: number } => {
  const prices = {
    main: 0,
    secondary: 0,
  };

  for (const product of productList) {
    prices.main += product.prices.main;
    prices.secondary += product.prices.secondary || 0;
  }

  return prices;
};

export const getCurrencyName = (): ECardCurrency =>
  isUkPlatform() ? ECardCurrency.GB : ECardCurrency.EU;

/**
 * Return if the seller is NOT selling the product from the same country as the platform.
 * @param seller Seller of the product
 * @returns Boolean
 */
export const isCrossSeller = (seller: Seller | undefined): boolean => {
  if (!seller || !isB2B) return false;

  return seller.country?.toLowerCase() !== getPlatform();
};

/**
 * Returns the price with or without VAT based on platform
 * @param price Product price (primary, secondary, or retail)
 * @returns number | undefined
 */
export const getPriceByPlatform = (price: Price | undefined | null) => {
  if (!price) return undefined;

  return !isB2B() || (isB2B() && isUkPlatform())
    ? price.amountWithVat
    : price.amountWithoutVat;
};

/**
 * Returns the price depending on the measurement
 * @param price ProductPrice to check
 * @returns primary or secondary Price
 */
export const primaryOrSecondaryPrice = (price: ProductPrice) =>
  price.primaryPrice.measurementUnit
    ? price.measurementPrice
    : price.primaryPrice;

/**
 * Returns the price of the provided productDetail according to the defined rules:
 * https://manomano.atlassian.net/wiki/spaces/MX/pages/3522888387/VAT+in+Product+Pages+Product+Cards
 * @param detail
 * @returns
 */
export const getProductPrice = (detail: Detail) => {
  if (!detail.price) return 0;

  const price = primaryOrSecondaryPrice(detail.price);

  return isCrossSeller(detail.seller)
    ? price?.amountWithVat
    : getPriceByPlatform(price);
};

export const getDetailPrice = (detail: Detail): Product['prices'] => {
  if (!detail.price)
    return {
      main: 0,
      secondary: 0,
      retail: 0,
    };

  const price = primaryOrSecondaryPrice(detail.price);

  return {
    main: price?.amountWithVat || 0,
    secondary: price?.amountWithoutVat || 0,
    retail: 0,
  };
};
